import { ReactNode } from 'react'
import { FormGroup, Modal } from 'react-bootstrap'
import FormHandler, { FormParams } from '../../utils/Form/FormHandler'
import Field from '../../utils/Form/Widgets/Field'
import AutocompleteSelect from '../../utils/Form/Widgets/AutocompleteSelect'
import Select from '../../utils/Form/Widgets/Select'
import { companySitesList } from '../charts/core/_requests'

type Props = {
  onHide: () => void
  show: boolean
  itemIdForUpdate?: string
  refreshFunction?: Function
}

class Profiles {
  name?: string
  description?: string
  company?: number
  company_site?: number
  meter_type?: 'Electricity' | 'Gas'
  start_date?: string
  end_date?: string
  profile_type?: 'event_based' | 'day_based'
  event_type?: number
  day?: string
  profile_data?: string
  profile_data_source?: number
  status?: 'created' | 'preparing' | 'active' | 'passive' | 'failed'
}

class ModalState {
  siteOptions?: {
    value: string
    label: string
  }[]
}

class ProfilesEditModal extends FormHandler {
  public state: FormParams & ModalState

  constructor(props: Props) {
    super(props)
    const initial = new Profiles()
    ;(initial as any).status = 'created'
    this.state = { entity: initial, siteOptions: [] }
  }

  componentDidUpdate(prevProps: Readonly<any>, prevState: Readonly<{}>, snapshot?: any): void {
    super.componentDidUpdate(prevProps, prevState, snapshot)
    let state = this.state as any
    let prev = prevState as any
    // Fetch sites when company changes OR when entity is first loaded
    if (prev.entity.company !== state.entity.company) {
      this.getSites()
    }
    // Also fetch sites when entity is loaded from API (for edit mode)
    if (!prev.entity.company && state.entity.company) {
      this.getSites()
    }
  }

  getSites() {
    let state = this.state as any
    if (state.entity.company) {
      companySitesList({ companyId: Number(state.entity.company) }).then((response) => {
        this.setState({
          ...this.state,
          siteOptions: response,
        })
      })
    } else {
      this.setState({
        ...this.state,
        siteOptions: [],
      })
    }
  }

  getApiUrl(): string {
    return '/profiles/save/' + this.props.itemIdForUpdate
  }
  getSingleUrl() {
    if (this.props.itemIdForUpdate === 'add') {
      return null
    } else {
      return '/profiles/single/' + this.props.itemIdForUpdate
    }
  }

  getPostData(): object {
    return {
      profiles: this.state.entity,
    }
  }
  render(): ReactNode {
    var state = this.state as any
    const isDayBased = state.entity.profile_type === 'day_based'
    const isEventBased = state.entity.profile_type === 'event_based'
    // If status is active or passive, all fields except status should be read-only
    const isReadOnly =
      this.props.itemIdForUpdate !== 'add' &&
      (state.entity.status === 'active' || state.entity.status === 'passive')
    return (
      <Modal
        aria-hidden='true'
        dialogClassName='modal-dialog modal-dialog-centered'
        size='lg'
        show={this.props.show}
        backdrop={true}
        onHide={this.props.onHide}
      >
        <form
          onSubmit={(event) => {
            this.handleSubmit(event).then((response) => {
              if (this.props.refreshFunction) {
                const id =
                  (response &&
                    response.data &&
                    response.data.data &&
                    response.data.data.entity_id) ||
                  (response && response.data && response.data.data && response.data.data.id) ||
                  this.props.itemIdForUpdate
                this.props.refreshFunction(id)
              }
              this.props.onHide()
            })
          }}
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {this.props.itemIdForUpdate === 'add' ? 'Add' : 'Edit'} Profile
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <FormGroup>
              <Field
                title='Name'
                className='form-control'
                type='text'
                name='name'
                onInput={(event: any) => {
                  this.handleInputChange(event)
                }}
                defaultValue={state.entity.name}
                disabled={isReadOnly}
              />
            </FormGroup>
            <FormGroup>
              <Field
                title='Description'
                className='form-control'
                type='text'
                name='description'
                onInput={(event: any) => {
                  this.handleInputChange(event)
                }}
                defaultValue={state.entity.description}
                disabled={isReadOnly}
              />
            </FormGroup>
            <FormGroup>
              <AutocompleteSelect
                key={`company-${this.props.itemIdForUpdate}-${state.entity.company}`}
                title='Company'
                name='company'
                onChange={(event: any) => {
                  this.handleInputChange(event)
                }}
                defaultValue={state.entity.company || 0}
                autoCompleteTokenUrl='/company/autocomplete'
                isDisabled={isReadOnly}
              />
            </FormGroup>
            <FormGroup>
              <Select
                title='Company Site'
                name='company_site'
                options={this.state.siteOptions || []}
                defaultValue={state.entity.company_site}
                onChange={(event: any) => {
                  this.handleInputChange(event)
                }}
                disabled={isReadOnly}
              />
            </FormGroup>
            <FormGroup>
              <Select
                title='Meter Type'
                name='meter_type'
                options={[
                  { value: 'Electricity', label: 'Electricity' },
                  { value: 'Gas', label: 'Gas' },
                ]}
                defaultValue={state.entity.meter_type}
                onChange={(event: any) => {
                  this.handleInputChange(event)
                }}
                disabled={isReadOnly}
              />
            </FormGroup>
            <div className='row'>
              <div className='col-md-6'>
                <FormGroup>
                  <Field
                    title='Start Date'
                    className='form-control'
                    type='date'
                    name='start_date'
                    onInput={(event: any) => {
                      this.handleInputChange(event)
                    }}
                    defaultValue={state.entity.start_date}
                    disabled={isReadOnly}
                  />
                </FormGroup>
              </div>
              <div className='col-md-6'>
                <FormGroup>
                  <Field
                    title='End Date'
                    className='form-control'
                    type='date'
                    name='end_date'
                    onInput={(event: any) => {
                      this.handleInputChange(event)
                    }}
                    defaultValue={state.entity.end_date}
                    disabled={isReadOnly}
                  />
                </FormGroup>
              </div>
            </div>
            <FormGroup>
              <div className='mb-3'>
                <label className='form-label fw-bold'>Profile Type</label>
                <div className='d-flex flex-wrap gap-3 mt-2'>
                  {[
                    { value: 'event_based', label: 'Event Based', icon: 'fa-calendar-alt' },
                    { value: 'day_based', label: 'Day Based', icon: 'fa-calendar-day' },
                  ].map((option) => (
                    <div
                      key={option.value}
                      className='form-check form-check-custom form-check-solid'
                    >
                      <input
                        className='form-check-input'
                        type='radio'
                        name='profile_type'
                        id={`profile_type_${option.value}`}
                        value={option.value}
                        checked={state.entity.profile_type === option.value}
                        onChange={(event: any) => {
                          this.handleInputChange(event)
                        }}
                        disabled={isReadOnly}
                      />
                      <label
                        className='form-check-label d-flex align-items-center'
                        htmlFor={`profile_type_${option.value}`}
                      >
                        <i className={`fa ${option.icon} me-2 text-primary`}></i>
                        {option.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </FormGroup>
            {isEventBased && (
              <FormGroup>
                <AutocompleteSelect
                  title='Event Type'
                  name='event_type'
                  onChange={(event: any) => {
                    this.handleInputChange(event)
                  }}
                  defaultValue={state.entity.event_type || 0}
                  autoCompleteTokenUrl='/event_types/autocomplete'
                  isDisabled={isReadOnly}
                />
              </FormGroup>
            )}
            {isDayBased && (
              <FormGroup>
                <Select
                  title='Day'
                  name='day'
                  options={[
                    { value: 'Monday', label: 'Monday' },
                    { value: 'Tuesday', label: 'Tuesday' },
                    { value: 'Wednesday', label: 'Wednesday' },
                    { value: 'Thursday', label: 'Thursday' },
                    { value: 'Friday', label: 'Friday' },
                    { value: 'Saturday', label: 'Saturday' },
                    { value: 'Sunday', label: 'Sunday' },
                  ]}
                  defaultValue={state.entity.day}
                  onChange={(event: any) => {
                    this.handleInputChange(event)
                  }}
                  disabled={isReadOnly}
                />
              </FormGroup>
            )}
            {/* profile_data and profile_data_source are filled by backend cron; not editable in UI */}
            {/* Status: only allow toggling between active/passive if current status is one of them; otherwise show read-only */}
            {this.props.itemIdForUpdate === 'add' ? (
              <></>
            ) : state.entity.status === 'active' || state.entity.status === 'passive' ? (
              <>
                {isReadOnly && (
                  <div className='alert alert-info d-flex align-items-center mb-3'>
                    <i className='fa fa-info-circle me-2'></i>
                    <span>This profile is {state.entity.status}. Only status can be changed.</span>
                  </div>
                )}
                <FormGroup>
                  <Select
                    title='Status'
                    name='status'
                    options={[
                      { value: 'active', label: 'Active' },
                      { value: 'passive', label: 'Passive' },
                    ]}
                    defaultValue={state.entity.status}
                    onChange={(event: any) => {
                      this.handleInputChange(event)
                    }}
                  />
                </FormGroup>
              </>
            ) : (
              <FormGroup>
                <Field
                  title='Status'
                  className='form-control'
                  type='text'
                  name='status'
                  defaultValue={state.entity.status}
                  onInput={() => {}}
                  disabled={true}
                />
              </FormGroup>
            )}
          </Modal.Body>
          <Modal.Footer className='justify-content-end'>
            {this.props.itemIdForUpdate !== 'add' && !isReadOnly ? (
              <button
                type='button'
                onClick={(event) => {
                  this.handleDelete(event).then((response) => {
                    if (this.props.refreshFunction) {
                      this.props.refreshFunction('')
                    }
                    this.props.onHide()
                  })
                }}
                className='btn btn-danger btn-sm'
              >
                <span className='fa fa-trash'></span>
                Delete
              </button>
            ) : (
              <></>
            )}
            <button type='submit' className='btn btn-success btn-sm'>
              <span className='fa fa-floppy-save'></span>
              Save
            </button>
          </Modal.Footer>
        </form>
      </Modal>
    )
  }
}

export default ProfilesEditModal
